import Service from '@ember/service';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';

const ORIGIN_AWARE_ROUTES = [
  'backlog.initiatives.initiative',
  'business.goals.goal',
];

function matchingOriginAwareRoute(
  routeName: string | null | undefined,
): string | undefined {
  if (!routeName) return undefined;
  return ORIGIN_AWARE_ROUTES.find((route) => routeName.startsWith(route));
}

export default class RouterHistoryService extends Service {
  @service declare router: RouterService;

  previousURL: string | null = null;
  previousRouteName: string | null = null;

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    this.router.on('routeWillChange', this._captureOutgoingURL);
    this.router.on('routeDidChange', this._patchOriginIntoURL);
  }

  willDestroy() {
    this.router.off('routeWillChange', this._captureOutgoingURL);
    this.router.off('routeDidChange', this._patchOriginIntoURL);
    super.willDestroy();
  }

  _captureOutgoingURL = (transition: Transition) => {
    if (transition.from) {
      this.previousRouteName = transition.from.name;
      this.previousURL = this.router.currentURL;
    }
  };

  _patchOriginIntoURL = () => {
    const currentMatch = matchingOriginAwareRoute(this.router.currentRouteName);
    if (!currentMatch || !this.previousURL) return;

    // Arriving from another tab, or another model of the same detail page
    // (e.g. swapping initiatives via the in-page selector), keeps whatever
    // origin is already recorded — Ember controllers are singletons, so
    // originUrl can be stale-but-present from an earlier visit; only an
    // arrival from outside this page's own family should refresh it.
    if (matchingOriginAwareRoute(this.previousRouteName) === currentMatch) {
      return;
    }

    this.router.replaceWith({ queryParams: { originUrl: this.previousURL } });
  };
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:router-history')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('router-history') declare altName: RouterHistoryService;`.
declare module '@ember/service' {
  interface Registry {
    'router-history': RouterHistoryService;
  }
}
